import { SUPPRESSION_SPEED_MULT } from '../../data/abilities.js';
import { SQUAD_DEFS } from '../../data/squads.js';
import { aStar } from '../../pathfinding/AStar.js';
import type { PathGrid } from '../../pathfinding/Grid.js';
import type { MatchState } from '../State.js';
import { TICK_MS } from '../State.js';

import { readTargetPos } from './CombatSystem.js';

const ARRIVAL_EPSILON_PX = 1.5;

// Halt-at-firing-range fires when the engaged target is something a squad
// should besiege rather than walk on top of:
//   - core / building — static, point-blank ruins LoS and looks broken
//     (this fixed the original "enemy stands on the core silently" bug).
//   - neutral_unit (camp guards) — the camp turret is permanently static
//     and patrols halt when aggroed (CampSystem), so during combat they're
//     effectively stationary too. Without this, the bot's "kill the camp
//     first" routing walked the squad straight into the patrol's position,
//     putting it in melee with multiple guards + the camp turret at once.
//
// Player squad targets deliberately keep the old "walk while firing"
// behaviour for *movement*: halting on every squad encounter changes
// match pacing (Tactical-vs-Tactical doctrine-pick tests regress
// because matches end before sim-second 180). 0.9 keeps a small
// standoff inside max range so a 1-px jitter doesn't toggle movement.
// squad.target is set by last tick's combatSystem; the one-tick
// staleness provides free hysteresis.
const ATTACK_MOVE_HALT_RANGE_MULT = 0.9;
// V1.9 — personal-space minimum separation against squad targets.
// Squads keep pushing toward their attack-move goal as before (so
// matches still pace correctly), but they refuse to enter the last
// SQUAD_PERSONAL_SPACE_PX in front of an engaged enemy squad. The
// effect is purely visual: instead of two squads stacking on the same
// pixel, they stop just short of touching and trade fire.
//
// 64 px ≈ 2× sprite radius — visible shoulder-room without changing
// who-wins-which-fight outcomes. We re-check engagement-targeting
// every tick, so a moving target naturally pulls the trailing squad
// behind it without ever overlapping.
const SQUAD_PERSONAL_SPACE_PX = 64;
const SQUAD_PERSONAL_SPACE_PX_SQ = SQUAD_PERSONAL_SPACE_PX * SQUAD_PERSONAL_SPACE_PX;

// Each tick: for every squad with an active 'move' order, ensure a path exists, then
// advance the squad along its waypoints at squad.speed. When the order is satisfied we
// pop it from the queue. The system never invents intent — it only consumes orderQueue.
export function movementSystem(state: MatchState, grid: PathGrid): void {
  const dtSec = TICK_MS / 1000;
  for (const squad of Object.values(state.squads)) {
    // V1.5+ Slice 2 — channeling squads (currently only Recon mid-Cut Link)
    // are immobilized for the channel duration. The order queue is left as-is
    // so the squad resumes its previous order when the channel completes
    // without an interrupt — but Simulation.applyCommand cancels the channel
    // before reaching here whenever the owner issues a new move/attack-move.
    if (squad.channelingAbility !== null) continue;
    const order = squad.orderQueue[0];
    if (order === undefined) {
      squad.path = [];
      squad.pathIndex = 0;
      continue;
    }
    if (order.kind !== 'move' && order.kind !== 'attack_move') continue;

    // Halt attack-move when in firing range of a static target (core,
    // building, neutral_unit camp guard). Order stays queued so the
    // squad resumes its goal automatically once the target dies
    // (squad.target → null).
    if (
      order.kind === 'attack_move' &&
      squad.target !== null &&
      (squad.target.kind === 'core'
        || squad.target.kind === 'building'
        || squad.target.kind === 'neutral_unit')
    ) {
      const targetPos = readTargetPos(state, squad.target);
      if (targetPos !== null) {
        const def = SQUAD_DEFS[squad.archetype];
        const haltRange = def.range * ATTACK_MOVE_HALT_RANGE_MULT;
        const dx = targetPos.x - squad.pos.x;
        const dy = targetPos.y - squad.pos.y;
        if (dx * dx + dy * dy <= haltRange * haltRange) continue;
      }
    }

    // V1.9 — personal-space halt for ANY engaged enemy squad (regardless
    // of order kind, so both bot attack-move and player move orders
    // benefit). Applies only to the LAST 64 px of approach so squads
    // close fully into engagement range, then refuse to overlap. This
    // fixes the V1.8 "bot walks on top of player squad" visual without
    // changing strategic outcomes — the engaged squad still fires at
    // full DPS while halted (range is 220-280 px, well above 64 px).
    if (squad.target !== null && squad.target.kind === 'squad') {
      const targetPos = readTargetPos(state, squad.target);
      if (targetPos !== null) {
        const dx = targetPos.x - squad.pos.x;
        const dy = targetPos.y - squad.pos.y;
        if (dx * dx + dy * dy <= SQUAD_PERSONAL_SPACE_PX_SQ) {
          // Face the target while halted so the squad reads as
          // "engaging" rather than "frozen mid-stride." Cheap — only
          // runs while inside the 64 px shell.
          squad.facing = Math.atan2(dy, dx);
          continue;
        }
      }
    }

    // Recompute path on first tick of an order (path empty) or if the goal moved (final
    // waypoint differs from order target).
    const last = squad.path.length > 0 ? squad.path[squad.path.length - 1] : undefined;
    const targetMoved =
      last === undefined ||
      Math.abs(last.x - order.target.x) > 0.001 ||
      Math.abs(last.y - order.target.y) > 0.001;
    if (targetMoved) {
      squad.path = aStar(grid, squad.pos, order.target);
      squad.pathIndex = 0;
      if (squad.path.length === 0) {
        // No route — drop the order rather than chasing forever.
        squad.orderQueue.shift();
        continue;
      }
    }

    // Suppressing Fire (Assault Q): -30% movement speed while suppressedUntilTick
    // hasn't expired. Other speed modifiers stack here in V1+ via the same pattern.
    const suppressed = squad.suppressedUntilTick > state.tick;
    const effectiveSpeed = suppressed ? squad.speed * SUPPRESSION_SPEED_MULT : squad.speed;
    let stepRemaining = effectiveSpeed * dtSec;
    while (stepRemaining > 0 && squad.pathIndex < squad.path.length) {
      const wp = squad.path[squad.pathIndex];
      if (wp === undefined) break;
      const dx = wp.x - squad.pos.x;
      const dy = wp.y - squad.pos.y;
      const dist = Math.hypot(dx, dy);
      if (dist <= ARRIVAL_EPSILON_PX) {
        squad.pos.x = wp.x;
        squad.pos.y = wp.y;
        squad.pathIndex += 1;
        continue;
      }
      squad.facing = Math.atan2(dy, dx);
      if (stepRemaining >= dist) {
        squad.pos.x = wp.x;
        squad.pos.y = wp.y;
        squad.pathIndex += 1;
        stepRemaining -= dist;
        continue;
      }
      const inv = 1 / dist;
      squad.pos.x += dx * inv * stepRemaining;
      squad.pos.y += dy * inv * stepRemaining;
      stepRemaining = 0;
    }

    if (squad.pathIndex >= squad.path.length) {
      // Order satisfied — drop it and clear the path so a fresh order replans.
      squad.orderQueue.shift();
      squad.path = [];
      squad.pathIndex = 0;
    }
  }
}
